/**
 * Feature key under which the widgets slice is registered in the root store
 * (mirrors the 'timesheetCollection' / 'addressCollection' feature keys).
 */
export const WIDGETS_FEATURE_KEY = 'widgets';

/**
 * The committed values behind the signal-forms section of the widget test screen.
 *
 * Only primitives live here so the slice satisfies the root store's
 * `strictStateSerializability` runtime check: a date is the HTML5 `yyyy-MM-dd`
 * string the native date input works with, never a `moment`. That is also what
 * lets the low-level field components stay domain-agnostic — they edit plain
 * strings, and conversion to the rich models happens at the edges of the store.
 *
 * The type grows by one property per low-level component as the remaining
 * signal-forms widgets (time, text, number, …) are added; it deliberately does
 * *not* grow by one action per field.
 */
export type WidgetsFormState = {
    /** HTML5 date (`yyyy-MM-dd`); empty string when no date is selected */
    dateValue: string;
    /** HTML5 time (`HH:mm`); empty string when no time is selected */
    timeValue: string;
    /** free text; empty string when nothing has been entered */
    textValue: string;
    /** free multi-line text; empty string when nothing has been entered */
    multilineTextValue: string;
    /**
     * numeric value; `null` when the field is empty.
     *
     * `null` rather than the repo's usual `undefined` for the same reason the
     * timesheet slice does it: the root store's strict serializability checks reject
     * explicitly undefined values but accept null. It is also the empty value signal
     * forms expect on a numeric path (`number | string | null`).
     */
    numberValue: number | null;
    /** stepped numeric value; `null` when the field is empty, same rationale as `numberValue` */
    numberStepperValue: number | null;
    /** tick state; a checkbox is never "empty", so this needs no null case */
    checkboxValue: boolean;
    /**
     * duration in **total minutes**; `null` when the field is empty.
     *
     * Not a `moment.Duration` — a class instance would fail the store's strict
     * serializability check — and not an ISO 8601 string, because minutes keep the
     * stepper arithmetic trivial and let the schema's `min()`/`max()` rules reach the
     * field through the `FormUiControl` contract.
     */
    durationStepperValue: number | null;
    /**
     * selected dropdown option values, in selection order.
     *
     * The first collection-valued field on the screen. An empty array is the empty
     * state — no null case needed — and the same shape serves single- and multi-select,
     * so switching a dropdown's mode never changes the slice.
     */
    dropdownValue: string[];
    /**
     * the selected radio option's value; empty string when nothing is chosen.
     *
     * A scalar rather than a collection, because a radio group is single-select by
     * definition — and unlike the dropdown, empty is unambiguous, so `required` applies.
     */
    radioValue: string;
    /**
     * the number of stars given; `null` when nothing has been rated.
     *
     * A number, unlike the legacy `RatingModel.value`, which is typed `string` while
     * holding a star count.
     */
    ratingValue: number | null;
    /**
     * selected tree *leaf* values, in selection order.
     *
     * Flat rather than hierarchical, because only leaves are selectable and there is no
     * parent-to-child cascade: a branch is never stored. Which branches are expanded is
     * view state and stays in the component.
     */
    treeValue: string[];
};

export type WidgetsState = Readonly<{
    /** the committed form values — the single source of truth for the signal form */
    form: WidgetsFormState;
    /**
     * Incremented by every write to `form`.
     *
     * The screen re-seeds its editable draft from `form` only when this changes,
     * so a store update that is not a commit — in the real timesheet slice, a
     * background rehydrate from storage — cannot silently discard what the user
     * is currently typing. See `WidgetsComponent.signalFormDraft`.
     */
    revision: number;
}>;

export const initialWidgetsState: WidgetsState = {
    // starts empty so the required-validation path is visible on first render
    form: {
        dateValue: '',
        timeValue: '',
        textValue: '',
        multilineTextValue: '',
        numberValue: null,
        numberStepperValue: null,
        checkboxValue: false,
        durationStepperValue: null,
        dropdownValue: [],
        radioValue: '',
        ratingValue: null,
        treeValue: [],
    },
    revision: 0,
};
